// src/pages/DriftAlarms.js
// Drift Alarms queue page — finding-centric alarm queue with drawer (PRD-48)

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useVirtualizer } from '@tanstack/react-virtual';
import CleanModernLayout from '../components/layouts/CleanModernLayout';
import { getAlarms, getEventsExport, dismissBaselineBanner } from '../services/driftService';
import { getAssets, getAsset } from '../services/assetService';
import { HelpIcon } from '../components/common';
import DriftAlarmDrawer from '../components/DriftAlarmDrawer';
import BulkAcceptModal from '../components/BulkAcceptModal';
import BulkIgnoreModal from '../components/BulkIgnoreModal';
import BaselineAcceptBanner from '../components/BaselineAcceptBanner';
import BaselineAcceptModal from '../components/BaselineAcceptModal';
import { BASELINE_BANNER_MIN_ASSET_CREATED_AT } from '../config/baselineConfig';
import './DriftAlarms.css';

// Severity chips (multi-select). Ordered most→least severe so the row reads the
// same direction as the default sort.
const SEVERITY_LEVELS = [
    { value: 'critical', label: 'Critical' },
    { value: 'high', label: 'High' },
    { value: 'medium', label: 'Medium' },
    { value: 'low', label: 'Low' },
    { value: 'info', label: 'Info' },
];
const ALL_SEVERITY_VALUES = SEVERITY_LEVELS.map(o => o.value);

// Parse the ?severity= param → a Set. Accepts a single value ("critical", the
// Command Center deep-link) or a comma list ("critical,high"). Empty/absent/all
// → the full set (= no filter). Invalid values are dropped.
function parseSeverityParam(raw) {
    if (!raw || raw === 'all') return new Set(ALL_SEVERITY_VALUES);
    const valid = raw.split(',').map(s => s.trim().toLowerCase()).filter(v => ALL_SEVERITY_VALUES.includes(v));
    return valid.length ? new Set(valid) : new Set(ALL_SEVERITY_VALUES);
}

const STATUS_OPTIONS = [
    { value: 'open',     label: 'Open' },
    { value: 'accepted', label: 'Accepted (Acceptable Risks)' },
    { value: 'ignored',  label: 'Ignored (False Positives)' },
    { value: 'resolved', label: 'Resolved' },
    { value: 'all',      label: 'All' },
];

// Higher weight = more severe, so the default `desc` sort (▼) puts Critical on
// top → High → Medium → Low → Info. Mirrors the backend api/shared/driftAlarms.js
// ordering (critical:4 … info:0). Unknown severities fall to the bottom (`?? -1`).
const SEVERITY_ORDER = { critical: 4, high: 3, medium: 2, low: 1, info: 0 };
const ROW_HEIGHT = 56;

function timeAgo(iso) {
    if (!iso) return '—';
    const then = new Date(iso).getTime();
    if (isNaN(then)) return iso;
    const diff = Date.now() - then;
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return `${Math.floor(days / 30)}mo ago`;
}

function daysUntil(iso) {
    if (!iso) return null;
    const then = new Date(iso).getTime();
    if (isNaN(then)) return null;
    return Math.ceil((then - Date.now()) / 86400000);
}

function DriftAlarms() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [alarms, setAlarms] = useState([]);
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Filters
    const [severitySet, setSeveritySet] = useState(() => parseSeverityParam(searchParams.get('severity')));
    const [filterStatus, setFilterStatus] = useState(searchParams.get('status') || 'open');
    const toggleSeverity = (sev) => setSeveritySet(prev => {
        const next = new Set(prev);
        if (next.has(sev)) next.delete(sev); else next.add(sev);
        return next;
    });
    // A partial selection (1..4 of 5) is an active constraint; empty or full = "all".
    const severityFilterActive = severitySet.size > 0 && severitySet.size < ALL_SEVERITY_VALUES.length;
    const [filterAsset, setFilterAsset] = useState(searchParams.get('assetId') || 'all');

    // Client-side text filters over the already-loaded set — a global quick
    // search plus per-column substring filters (e.g. type "EOL" under Finding
    // Name). They compose with the server-side status/asset scope and the
    // client-side severity chips.
    const [globalSearch, setGlobalSearch] = useState(searchParams.get('q') || '');
    const [columnFilters, setColumnFilters] = useState(() => ({
        name: searchParams.get('fName') || '',
        location: searchParams.get('fLoc') || '',
    }));
    const [showColumnFilters, setShowColumnFilters] = useState(
        () => !!(searchParams.get('fName') || searchParams.get('fLoc'))
    );
    const setColumnFilter = (key, val) => setColumnFilters(prev => ({ ...prev, [key]: val }));
    const activeColumnFilterCount = Object.values(columnFilters).filter(v => v.trim()).length;
    const clearColumnFilters = () => setColumnFilters({ name: '', location: '' });
    // Any client-side (post-fetch) filter is active — used to keep the table
    // shell (and its column-filter inputs) mounted even when the result is empty.
    const clientFiltered = severityFilterActive || !!globalSearch.trim() || activeColumnFilterCount > 0;
    const clearAllClientFilters = () => {
        setSeveritySet(new Set(ALL_SEVERITY_VALUES));
        setGlobalSearch('');
        clearColumnFilters();
    };

    // Sorting (PRD-80 v3 Amendment F: virtualized table, no pagination)
    const [sortKey, setSortKey] = useState(searchParams.get('sort') || 'severity');
    const [sortDir, setSortDir] = useState(searchParams.get('dir') === 'asc' ? 'asc' : 'desc');

    // Drawer
    const [drawerAlarmId, setDrawerAlarmId] = useState(searchParams.get('alarmId') || null);

    // Selection — Amendment F made the legacy "select-all-matching across
    // pagination boundary" UX unnecessary since the virtualized list shows the
    // entire processedAlarms set. The truncation edge case (API returns >2000
    // rows) is not exercised by any current user and is deferred to follow-up.
    const [selectedAlarms, setSelectedAlarms] = useState(new Set());

    // Bulk modals
    const [bulkModalType, setBulkModalType] = useState(null); // 'accept' | 'ignore' | null
    const [bulkToast, setBulkToast] = useState(null);

    // PRD-63 WP1: baseline banner state — populated when a specific asset is filtered
    const [selectedAssetMeta, setSelectedAssetMeta] = useState(null);
    const [baselineModalOpen, setBaselineModalOpen] = useState(false);
    const [baselineDismissing, setBaselineDismissing] = useState(false);
    const [baselineSuccess, setBaselineSuccess] = useState(null); // { count, batchId, acceptedAt }

    // Export
    const [exporting, setExporting] = useState(false);

    // Build asset name map — `value` is the canonical display field (e.g.
    // "beqom.com" / "164.44.37.1/24"); fall back through legacy names then
    // assetId so the dropdown never shows raw IDs.
    const assetMap = useMemo(() => {
        const m = {};
        assets.forEach(a => { m[a.assetId] = a.assetName || a.value || a.target || a.assetId; });
        return m;
    }, [assets]);

    // Load alarms
    // Status filter is single source of truth: 'open' shows only open, anything else
    // requires includeDispositioned=true so accepted/ignored/resolved rows are fetched.
    const loadAlarms = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            // Severity is filtered client-side now (multi-select chips), so we
            // fetch the full severity range once and never refetch on a chip toggle.
            const opts = { includeDispositioned: filterStatus !== 'open' };
            if (filterAsset !== 'all') opts.assetId = filterAsset;
            if (filterStatus !== 'open' && filterStatus !== 'all') opts.status = filterStatus;
            const data = await getAlarms(opts);
            setAlarms(data);
        } catch (err) {
            setError(err.message || 'Failed to load alarms');
        } finally {
            setLoading(false);
        }
    }, [filterStatus, filterAsset]);

    useEffect(() => { loadAlarms(); }, [loadAlarms]);

    // Load assets for filter dropdown
    useEffect(() => {
        (async () => {
            try {
                const data = await getAssets();
                setAssets(data.assets || data || []);
            } catch { /* ignore */ }
        })();
    }, []);

    // PRD-63 WP1: load selected asset's full metadata (baseline fields,
    // currentRiskScore, createdAt) whenever the asset filter changes to a
    // specific asset. The /api/assets list response doesn't carry the
    // baseline fields, so we hit /api/asset-get/{assetId} for them.
    useEffect(() => {
        if (filterAsset === 'all') {
            setSelectedAssetMeta(null);
            setBaselineSuccess(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const resp = await getAsset(filterAsset);
                if (cancelled) return;
                // /asset-get returns { success, data: { asset: {...} } }
                const a = resp?.data?.asset || resp?.asset || resp;
                setSelectedAssetMeta(a);
                // Clear any stale success row when switching assets
                setBaselineSuccess(null);
            } catch {
                if (!cancelled) setSelectedAssetMeta(null);
            }
        })();
        return () => { cancelled = true; };
    }, [filterAsset]);

    // Decide whether to render the baseline banner. ALL conditions must hold:
    //  - a specific asset is selected (banner is per-asset)
    //  - asset has not been baselined yet
    //  - user hasn't dismissed the banner for this asset
    //  - asset was created AFTER the deploy cutoff (so existing customers'
    //    long-standing assets don't get prompted to baseline on day 1)
    //  - there are open alarms to show
    const openCountForBanner = useMemo(
        () => alarms.filter(a => a.status === 'open').length,
        [alarms]
    );
    const shouldShowBaselineBanner = useMemo(() => {
        if (filterAsset === 'all') return false;
        if (!selectedAssetMeta) return false;
        if (selectedAssetMeta.baselineAcceptedAt) return false;
        if (selectedAssetMeta.baselineBannerDismissedAt) return false;
        if (baselineSuccess) return false;
        const createdAt = selectedAssetMeta.createdAt;
        if (!createdAt) return false;
        if (new Date(createdAt).getTime() <= new Date(BASELINE_BANNER_MIN_ASSET_CREATED_AT).getTime()) {
            return false;
        }
        if (openCountForBanner < 1) return false;
        return true;
    }, [filterAsset, selectedAssetMeta, openCountForBanner, baselineSuccess]);

    const handleDismissBaseline = useCallback(async () => {
        if (!selectedAssetMeta?.assetId) return;
        // Optimistic dismissal — hide the banner immediately, restore on failure
        const snapshot = selectedAssetMeta;
        setBaselineDismissing(true);
        setSelectedAssetMeta({ ...snapshot, baselineBannerDismissedAt: new Date().toISOString() });
        try {
            await dismissBaselineBanner(selectedAssetMeta.assetId);
        } catch (err) {
            // Restore + surface
            setSelectedAssetMeta(snapshot);
            setBulkToast({ kind: 'warn', message: 'Failed to dismiss banner — please try again' });
        } finally {
            setBaselineDismissing(false);
        }
    }, [selectedAssetMeta]);

    const handleBaselineSuccess = useCallback((payload) => {
        // Stamp the asset meta locally so the banner gate flips immediately
        if (selectedAssetMeta) {
            setSelectedAssetMeta({
                ...selectedAssetMeta,
                baselineAcceptedAt: payload.acceptedAt,
                baselineAcceptedCount: payload.count,
                baselineAcceptedBulkBatchId: payload.bulkBatchId,
            });
        }
        setBaselineSuccess({
            count: payload.count,
            bulkBatchId: payload.bulkBatchId,
            acceptedAt: payload.acceptedAt,
            assetName: selectedAssetMeta?.assetName || selectedAssetMeta?.value || selectedAssetMeta?.target || selectedAssetMeta?.assetId,
        });
        // Refresh the alarm list so the just-accepted rows leave the Open view
        loadAlarms();
    }, [selectedAssetMeta, loadAlarms]);

    const viewRiskAcceptanceRegister = useCallback(() => {
        // Deep-link to the Accepted view for this asset
        setFilterStatus('accepted');
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('status', 'accepted');
            if (filterAsset !== 'all') next.set('assetId', filterAsset);
            return next;
        });
    }, [filterAsset, setSearchParams]);

    // Deep-link: open drawer from URL
    useEffect(() => {
        const alarmIdParam = searchParams.get('alarmId');
        if (alarmIdParam && !drawerAlarmId) {
            setDrawerAlarmId(alarmIdParam);
        }
    }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

    // Persist filter + sort state to the URL so a view is refresh-stable and
    // shareable. Merge-safe (only touches its own keys, preserving alarmId etc.);
    // { replace: true } so high-frequency edits (typing) don't spam history.
    // Params are omitted when they equal their default to keep URLs clean.
    useEffect(() => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            const setOrDel = (key, val) => { if (val) next.set(key, val); else next.delete(key); };
            setOrDel('assetId', filterAsset !== 'all' ? filterAsset : '');
            setOrDel('status', filterStatus !== 'open' ? filterStatus : '');
            setOrDel('severity', severityFilterActive
                ? ALL_SEVERITY_VALUES.filter(v => severitySet.has(v)).join(',')
                : '');
            setOrDel('sort', sortKey !== 'severity' ? sortKey : '');
            setOrDel('dir', sortDir !== 'desc' ? sortDir : '');
            setOrDel('q', globalSearch.trim());
            setOrDel('fName', columnFilters.name.trim());
            setOrDel('fLoc', columnFilters.location.trim());
            return next;
        }, { replace: true });
    }, [filterAsset, filterStatus, severitySet, severityFilterActive, sortKey, sortDir, globalSearch, columnFilters, setSearchParams]);

    // Filtered + sorted alarms
    const processedAlarms = useMemo(() => {
        let list = [...alarms];

        // Status filter — exact-match for concrete values, no-op for 'all'
        if (filterStatus !== 'all') {
            list = list.filter(a => a.status === filterStatus);
        }
        // Severity multi-select (chips) — a partial selection filters; empty or
        // full set is a no-op ("all severities").
        if (severityFilterActive) {
            list = list.filter(a => severitySet.has(a.severity));
        }

        // Global quick-search — case-insensitive substring across name +
        // templateId + host/location (matchedAt).
        const gq = globalSearch.trim().toLowerCase();
        if (gq) {
            list = list.filter(a =>
                `${a.name || ''} ${a.templateId || ''} ${a.matchedAt || ''}`.toLowerCase().includes(gq)
            );
        }

        // Per-column text filters — substring, case-insensitive. Finding Name
        // matches the displayed name plus its templateId fallback; Host/Location
        // matches matchedAt (same fields the columns render).
        const nameQ = columnFilters.name.trim().toLowerCase();
        if (nameQ) {
            list = list.filter(a => `${a.name || ''} ${a.templateId || ''}`.toLowerCase().includes(nameQ));
        }
        const locationQ = columnFilters.location.trim().toLowerCase();
        if (locationQ) {
            list = list.filter(a => (a.matchedAt || '').toLowerCase().includes(locationQ));
        }

        // Sort
        list.sort((a, b) => {
            let valA, valB;
            switch (sortKey) {
                case 'severity':
                    valA = SEVERITY_ORDER[a.severity] ?? -1;
                    valB = SEVERITY_ORDER[b.severity] ?? -1;
                    break;
                case 'name':
                    valA = (a.name || '').toLowerCase();
                    valB = (b.name || '').toLowerCase();
                    return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
                case 'location':
                    valA = (a.matchedAt || '').toLowerCase();
                    valB = (b.matchedAt || '').toLowerCase();
                    return sortDir === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA);
                case 'detectionCount':
                    valA = a.detectionCount || 0;
                    valB = b.detectionCount || 0;
                    break;
                case 'firstDetectedAt':
                    valA = new Date(a.firstDetectedAt || 0).getTime();
                    valB = new Date(b.firstDetectedAt || 0).getTime();
                    break;
                case 'lastDetectedAt':
                default:
                    valA = new Date(a.lastDetectedAt || 0).getTime();
                    valB = new Date(b.lastDetectedAt || 0).getTime();
                    break;
            }
            return sortDir === 'asc' ? valA - valB : valB - valA;
        });

        return list;
    }, [alarms, filterStatus, severitySet, severityFilterActive, columnFilters, globalSearch, sortKey, sortDir]);

    const openCount = alarms.filter(a => a.status === 'open').length;

    // Drift-only banner — surface when the visible alarms are all drift-* (no
    // vulnerability findings) so users don't read "open alarms" as "open vulns."
    const driftOnly = processedAlarms.length > 0
        && processedAlarms.every(a => (a.templateCategory || '').startsWith('drift-'));

    // Virtualized scrolling — replaces the old 25-row pager so high-volume
    // assets (e.g. beqom with 487+ open) are fully reachable via scroll.
    const scrollParentRef = useRef(null);
    const rowVirtualizer = useVirtualizer({
        count: processedAlarms.length,
        getScrollElement: () => scrollParentRef.current,
        estimateSize: () => ROW_HEIGHT,
        overscan: 8,
    });

    // Column sort handler
    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortKey(key);
            setSortDir('desc');
        }
    };

    const sortIcon = (key) => {
        if (sortKey !== key) return '';
        return sortDir === 'asc' ? ' ▲' : ' ▼';
    };

    // Row click → open drawer
    const handleRowClick = (alarm) => {
        setDrawerAlarmId(alarm.alarmId);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.set('alarmId', alarm.alarmId);
            return next;
        }, { replace: true });
    };

    // Drawer close
    const handleDrawerClose = () => {
        setDrawerAlarmId(null);
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            next.delete('alarmId');
            return next;
        }, { replace: true });
    };

    // After successful action in drawer
    const handleAlarmActioned = () => {
        handleDrawerClose();
        loadAlarms();
    };

    // Selection helpers — header checkbox selects ALL filtered rows (the
    // virtualized list shows the full processedAlarms set).
    const allSelected = processedAlarms.length > 0 && processedAlarms.every(a => selectedAlarms.has(a.alarmId));
    const toggleSelectAll = () => {
        setSelectedAlarms(allSelected ? new Set() : new Set(processedAlarms.map(a => a.alarmId)));
    };
    const toggleSelect = (alarmId, e) => {
        e.stopPropagation();
        setSelectedAlarms(prev => {
            const next = new Set(prev);
            if (next.has(alarmId)) next.delete(alarmId);
            else next.add(alarmId);
            return next;
        });
    };
    const clearSelection = () => setSelectedAlarms(new Set());

    // Bulk action IDs
    const bulkAlarmIds = useMemo(() => [...selectedAlarms], [selectedAlarms]);

    // Smart bulk scope default — if ≥3 selected alarms (that we have loaded)
    // share the same templateId, suggest asset-wide scope so the user doesn't
    // accept each detection individually. Caller surfaces this via a notice.
    const { bulkDefaultScope, bulkSmartScopeReason } = useMemo(() => {
        if (bulkAlarmIds.length < 3) return { bulkDefaultScope: 'host', bulkSmartScopeReason: null };
        const idSet = new Set(bulkAlarmIds);
        const selectedLoaded = alarms.filter(a => idSet.has(a.alarmId));
        const counts = selectedLoaded.reduce((acc, a) => {
            const key = a.templateId || '';
            if (!key) return acc;
            acc[key] = (acc[key] || 0) + 1;
            return acc;
        }, {});
        const [topKey, topCount] = Object.entries(counts).sort((a, b) => b[1] - a[1])[0] || [null, 0];
        if (topCount >= 3) {
            return {
                bulkDefaultScope: 'asset',
                bulkSmartScopeReason: `${topCount} of the selected alarms share "${topKey}" — defaulting to asset-wide.`,
            };
        }
        return { bulkDefaultScope: 'host', bulkSmartScopeReason: null };
    }, [bulkAlarmIds, alarms]);

    // Bulk action success handler
    const handleBulkSuccess = (result) => {
        const { succeeded, failed, bulkBatchId } = result;
        clearSelection();
        loadAlarms();
        if (failed > 0) {
            setBulkToast({ type: 'partial', succeeded, failed, bulkBatchId });
        } else {
            setBulkToast({ type: 'success', succeeded, bulkBatchId });
        }
        setTimeout(() => setBulkToast(null), 8000);
    };

    // Export
    const handleExport = async (format) => {
        setExporting(true);
        try {
            await getEventsExport(format);
        } catch (err) {
            console.error('Export failed:', err);
        } finally {
            setExporting(false);
        }
    };

    return (
        <CleanModernLayout activePage="drift-alarms">
            <div className="da-page">
                {/* Header */}
                <div className="da-header">
                    <div className="da-header__title">
                        <h1>Drift Alarms</h1>
                        <HelpIcon content="Drift alarms fire when your attack surface changes. Review findings, understand impact, and decide: Accept (known risk) or Ignore (false positive)." category="context" />
                        {!loading && (
                            <span className="da-header__badge">{openCount} open</span>
                        )}
                    </div>
                </div>

                {/* Error Banner */}
                {error && (
                    <div className="da-error">
                        {error}
                        <button className="da-error__dismiss" onClick={() => setError(null)}>Dismiss</button>
                    </div>
                )}

                {/* PRD-63 WP1: Baseline accept banner (per-asset, gated by createdAt cutoff) */}
                {shouldShowBaselineBanner && (
                    <BaselineAcceptBanner
                        asset={selectedAssetMeta}
                        openAlarmCount={openCountForBanner}
                        onAcceptAll={() => setBaselineModalOpen(true)}
                        onDismiss={handleDismissBaseline}
                        dismissing={baselineDismissing}
                    />
                )}

                {/* PRD-63 WP1: Post-baseline confirmation */}
                {baselineSuccess && (
                    <div className="baseline-success" role="status">
                        <span className="baseline-success__check">✓</span>
                        <div className="baseline-success__body">
                            <strong>Baseline accepted on {new Date(baselineSuccess.acceptedAt).toLocaleDateString()}.</strong>
                            {' '}{baselineSuccess.count} alarm{baselineSuccess.count !== 1 ? 's' : ''} accepted on{' '}
                            <strong>{baselineSuccess.assetName}</strong>. From now on you'll see only NEW changes.
                            {' '}
                            <button
                                type="button"
                                className="baseline-success__link"
                                onClick={viewRiskAcceptanceRegister}
                            >
                                View your Risk Acceptance Register →
                            </button>
                            <span className="baseline-success__batch">Batch {baselineSuccess.bulkBatchId}</span>
                        </div>
                    </div>
                )}

                {/* Filter Bar */}
                <div className="da-filters">
                    <div className="da-filter-group">
                        <label className="da-filter-label">Asset</label>
                        <select
                            className="da-filter-select"
                            value={filterAsset}
                            onChange={e => setFilterAsset(e.target.value)}
                        >
                            <option value="all">All Assets</option>
                            {assets.map(a => (
                                <option key={a.assetId} value={a.assetId}>{a.assetName || a.value || a.target || a.assetId}</option>
                            ))}
                        </select>
                    </div>
                    <div className="da-filter-group da-filter-group--severity">
                        <label className="da-filter-label">Severity</label>
                        <div className="da-sev-chips" role="group" aria-label="Filter by severity">
                            {SEVERITY_LEVELS.map(o => {
                                const on = severitySet.has(o.value);
                                return (
                                    <button
                                        key={o.value}
                                        type="button"
                                        className={`da-sev-chip ${on ? 'on' : ''}`}
                                        data-severity={o.value}
                                        aria-pressed={on}
                                        onClick={() => toggleSeverity(o.value)}
                                    >
                                        <span className="da-sev-chip__dot" />
                                        {o.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    <div className="da-filter-group">
                        <label className="da-filter-label">Status</label>
                        <select
                            className="da-filter-select"
                            value={filterStatus}
                            onChange={e => setFilterStatus(e.target.value)}
                        >
                            {STATUS_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>
                    <div className="da-filter-group da-filter-group--search">
                        <label className="da-filter-label" htmlFor="da-global-search">Search</label>
                        <div className="da-search-wrap">
                            <span className="da-search-icon" aria-hidden="true">⌕</span>
                            <input
                                id="da-global-search"
                                className="da-search-input"
                                type="text"
                                placeholder="Search name, host…"
                                value={globalSearch}
                                onChange={e => setGlobalSearch(e.target.value)}
                            />
                            {globalSearch && (
                                <button
                                    type="button"
                                    className="da-search-clear"
                                    onClick={() => setGlobalSearch('')}
                                    aria-label="Clear search"
                                >
                                    ✕
                                </button>
                            )}
                        </div>
                    </div>
                    <div className="da-filter-group da-filter-group--export">
                        <button
                            className={`da-col-filter-toggle ${showColumnFilters || activeColumnFilterCount ? 'active' : ''}`}
                            onClick={() => setShowColumnFilters(s => !s)}
                            aria-pressed={showColumnFilters}
                            title="Show per-column search filters"
                        >
                            ⌕ Column Filters{activeColumnFilterCount ? ` (${activeColumnFilterCount})` : ''}
                        </button>
                        <button className="da-export-btn" onClick={() => handleExport('csv')} disabled={exporting} title="Downloads all alarms (not scoped to the current filters)">
                            {exporting ? '...' : 'Export all (CSV)'}
                        </button>
                    </div>
                </div>

                {/* Result count — shown when a client-side filter is trimming the set */}
                {!loading && (severityFilterActive || globalSearch.trim() || activeColumnFilterCount > 0) && (
                    <div className="da-result-count" role="status" aria-live="polite">
                        Filtered: <strong>{processedAlarms.length}</strong> of {alarms.length}
                        <button
                            type="button"
                            className="da-result-count__clear"
                            onClick={clearAllClientFilters}
                        >
                            Clear filters
                        </button>
                    </div>
                )}

                {/* Table */}
                {loading ? (
                    <div className="da-loading">Loading alarms...</div>
                ) : processedAlarms.length === 0 && !clientFiltered ? (
                    // Truly empty (nothing to filter) — full empty state.
                    <div className="da-empty">
                        <p>No drift alarms found</p>
                        <span className="da-empty__sub">
                            {filterStatus === 'open' ? 'Your attack surface is stable — no open alarms.' : 'No alarms match current filters.'}
                        </span>
                    </div>
                ) : (
                    <>
                        {driftOnly && (
                            <div className="da-drift-only-banner" role="status">
                                <span className="da-drift-only-banner__icon">ℹ︎</span>
                                <div className="da-drift-only-banner__body">
                                    {filterAsset !== 'all' && selectedAssetMeta ? (
                                        <>
                                            <strong>No active vulnerability findings for {selectedAssetMeta.assetName || selectedAssetMeta.value || selectedAssetMeta.target || selectedAssetMeta.assetId}</strong>
                                            {' '}— only configuration-drift alarms shown.
                                            {' '}Last vuln-scan: {selectedAssetMeta.lastVulnScanAt ? timeAgo(selectedAssetMeta.lastVulnScanAt) : 'never'}.
                                        </>
                                    ) : (
                                        <>
                                            <strong>No active vulnerability alarms across your assets</strong>
                                            {' '}— only configuration-drift alarms shown.
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                        {/* Virtualized table (Amendment F) — div-based grid for
                            full-list scroll without pagination. Column widths
                            shared between header + body via CSS variable. */}
                        <div
                            ref={scrollParentRef}
                            className="da-table-scroll"
                            role="table"
                            aria-rowcount={processedAlarms.length}
                        >
                            <div className="da-table-grid">
                              <div className="da-grid-thead" role="rowgroup">
                                <div className="da-grid-header" role="row">
                                    <div className="da-grid-cell da-grid-cell--checkbox" role="columnheader">
                                        <input
                                            type="checkbox"
                                            className="da-checkbox"
                                            checked={allSelected}
                                            onChange={toggleSelectAll}
                                            aria-label="Select all filtered alarms"
                                        />
                                    </div>
                                    <div className={`da-grid-cell da-grid-cell--header ${sortKey === 'severity' ? 'sorted' : ''}`} onClick={() => handleSort('severity')} role="columnheader">
                                        Severity{sortIcon('severity')}
                                    </div>
                                    <div className={`da-grid-cell da-grid-cell--header ${sortKey === 'name' ? 'sorted' : ''}`} onClick={() => handleSort('name')} role="columnheader">
                                        Finding Name{sortIcon('name')}
                                    </div>
                                    <div className={`da-grid-cell da-grid-cell--header ${sortKey === 'location' ? 'sorted' : ''}`} onClick={() => handleSort('location')} role="columnheader">
                                        Host / Location{sortIcon('location')}
                                    </div>
                                    <div className={`da-grid-cell da-grid-cell--header ${sortKey === 'firstDetectedAt' ? 'sorted' : ''}`} onClick={() => handleSort('firstDetectedAt')} role="columnheader">
                                        First Seen{sortIcon('firstDetectedAt')}
                                    </div>
                                    <div className={`da-grid-cell da-grid-cell--header ${sortKey === 'lastDetectedAt' ? 'sorted' : ''}`} onClick={() => handleSort('lastDetectedAt')} role="columnheader">
                                        Last Seen{sortIcon('lastDetectedAt')}
                                    </div>
                                    <div className={`da-grid-cell da-grid-cell--header ${sortKey === 'detectionCount' ? 'sorted' : ''}`} onClick={() => handleSort('detectionCount')} role="columnheader">
                                        Count{sortIcon('detectionCount')}
                                    </div>
                                    <div className="da-grid-cell da-grid-cell--header" role="columnheader">Status</div>
                                </div>
                                {showColumnFilters && (
                                    <div className="da-grid-filter-row" role="row">
                                        <div className="da-grid-cell da-grid-cell--filter" />
                                        <div className="da-grid-cell da-grid-cell--filter" />
                                        <div className="da-grid-cell da-grid-cell--filter">
                                            <input
                                                className="da-col-filter"
                                                type="text"
                                                placeholder="e.g. EOL, TLS…"
                                                value={columnFilters.name}
                                                onChange={e => setColumnFilter('name', e.target.value)}
                                                aria-label="Filter by Finding Name"
                                            />
                                        </div>
                                        <div className="da-grid-cell da-grid-cell--filter">
                                            <input
                                                className="da-col-filter"
                                                type="text"
                                                placeholder="host / path…"
                                                value={columnFilters.location}
                                                onChange={e => setColumnFilter('location', e.target.value)}
                                                aria-label="Filter by Host / Location"
                                            />
                                        </div>
                                        <div className="da-grid-cell da-grid-cell--filter" />
                                        <div className="da-grid-cell da-grid-cell--filter" />
                                        <div className="da-grid-cell da-grid-cell--filter" />
                                        <div className="da-grid-cell da-grid-cell--filter da-grid-cell--filter-clear">
                                            {activeColumnFilterCount > 0 && (
                                                <button
                                                    className="da-col-filter-clear"
                                                    onClick={clearColumnFilters}
                                                    title="Clear column filters"
                                                    aria-label="Clear column filters"
                                                >
                                                    ✕
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                              </div>
                                <div
                                    className="da-grid-body"
                                    style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}
                                >
                                    {rowVirtualizer.getVirtualItems().map(virtualRow => {
                                        const alarm = processedAlarms[virtualRow.index];
                                        const isSelected = selectedAlarms.has(alarm.alarmId);
                                        const isDispositioned = alarm.status === 'accepted' || alarm.status === 'ignored';
                                        const expiryDays = isDispositioned ? daysUntil(alarm.dispositionExpiresAt) : null;
                                        return (
                                            <div
                                                key={alarm.alarmId}
                                                role="row"
                                                aria-rowindex={virtualRow.index + 1}
                                                className={`da-grid-row ${isSelected ? 'da-grid-row--selected' : ''} ${isDispositioned ? 'da-grid-row--muted' : ''}`}
                                                style={{
                                                    position: 'absolute',
                                                    top: 0,
                                                    left: 0,
                                                    right: 0,
                                                    transform: `translateY(${virtualRow.start}px)`,
                                                    height: `${virtualRow.size}px`,
                                                }}
                                                onClick={() => handleRowClick(alarm)}
                                            >
                                                <div className="da-grid-cell da-grid-cell--checkbox" onClick={e => e.stopPropagation()} role="cell">
                                                    <input
                                                        type="checkbox"
                                                        className="da-checkbox"
                                                        checked={isSelected}
                                                        onChange={e => toggleSelect(alarm.alarmId, e)}
                                                        aria-label={`Select alarm ${alarm.name || alarm.alarmId}`}
                                                    />
                                                </div>
                                                <div className="da-grid-cell" role="cell">
                                                    <span className="da-severity" data-severity={alarm.severity}>
                                                        <span className="da-severity__dot"></span>
                                                        {alarm.severity}
                                                    </span>
                                                </div>
                                                <div className="da-grid-cell da-grid-cell--name" title={alarm.description || alarm.name} role="cell">
                                                    {alarm.name || alarm.templateId || '—'}
                                                </div>
                                                <div className="da-grid-cell da-grid-cell--host" role="cell">
                                                    {alarm.matchedAt || '—'}
                                                </div>
                                                <div className="da-grid-cell da-grid-cell--time" role="cell">
                                                    {timeAgo(alarm.firstDetectedAt)}
                                                </div>
                                                <div className="da-grid-cell da-grid-cell--time" role="cell">
                                                    {timeAgo(alarm.lastDetectedAt)}
                                                </div>
                                                <div className="da-grid-cell da-grid-cell--count" role="cell">
                                                    {alarm.detectionCount || 1}
                                                </div>
                                                <div className="da-grid-cell" role="cell">
                                                    <span className="da-status" data-status={alarm.status}>
                                                        {alarm.status}
                                                    </span>
                                                    {isDispositioned && expiryDays !== null && (
                                                        <span className={`da-expiry ${expiryDays < 7 ? 'da-expiry--soon' : ''}`}>
                                                            expires in {expiryDays}d
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {processedAlarms.length === 0 && (
                                    <div className="da-grid-empty" role="status">
                                        No alarms match the current filters.{' '}
                                        <button type="button" className="da-result-count__clear" onClick={clearAllClientFilters}>
                                            Clear filters
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>


                        {/* Bulk action bar */}
                        {selectedAlarms.size > 0 && (
                            <div className="da-bulk-bar">
                                <span className="da-bulk-count">{selectedAlarms.size} selected</span>
                                <button className="da-bulk-btn da-bulk-btn--accept" onClick={() => setBulkModalType('accept')}>
                                    Accept selected
                                </button>
                                <button className="da-bulk-btn da-bulk-btn--ignore" onClick={() => setBulkModalType('ignore')}>
                                    Ignore selected
                                </button>
                                <button className="da-bulk-btn da-bulk-btn--clear" onClick={clearSelection}>
                                    Clear
                                </button>
                            </div>
                        )}
                    </>
                )}

                {/* Bulk toast */}
                {bulkToast && (
                    <div className={`da-toast ${bulkToast.type === 'partial' ? 'da-toast--warn' : 'da-toast--success'}`}>
                        {bulkToast.type === 'success' ? (
                            <span>
                                {bulkToast.succeeded} alarm{bulkToast.succeeded !== 1 ? 's' : ''} dispositioned.{' '}
                                <a href={`/suppressions?bulkBatchId=${bulkToast.bulkBatchId}`} className="da-toast__link">
                                    Undo batch
                                </a>
                            </span>
                        ) : (
                            <span>
                                {bulkToast.succeeded} of {bulkToast.succeeded + bulkToast.failed} succeeded — {bulkToast.failed} failed.{' '}
                                <a href={`/suppressions?bulkBatchId=${bulkToast.bulkBatchId}`} className="da-toast__link">
                                    View batch
                                </a>
                            </span>
                        )}
                        <button className="da-toast__dismiss" onClick={() => setBulkToast(null)}>×</button>
                    </div>
                )}

                {/* Drawer */}
                {drawerAlarmId && (
                    <DriftAlarmDrawer
                        alarmId={drawerAlarmId}
                        assetMap={assetMap}
                        onClose={handleDrawerClose}
                        onAlarmActioned={handleAlarmActioned}
                    />
                )}

                {/* Bulk modals */}
                <BulkAcceptModal
                    isOpen={bulkModalType === 'accept'}
                    onClose={() => setBulkModalType(null)}
                    alarmIds={bulkAlarmIds}
                    onSuccess={handleBulkSuccess}
                    defaultScope={bulkDefaultScope}
                    smartScopeReason={bulkSmartScopeReason}
                />
                <BulkIgnoreModal
                    isOpen={bulkModalType === 'ignore'}
                    onClose={() => setBulkModalType(null)}
                    alarmIds={bulkAlarmIds}
                    onSuccess={handleBulkSuccess}
                />

                {/* PRD-63 WP1: baseline-accept modal — passes the FULL set of
                    open alarmIds for the selected asset (acceptAllAsBaseline
                    handles chunking internally). */}
                <BaselineAcceptModal
                    isOpen={baselineModalOpen}
                    onClose={() => setBaselineModalOpen(false)}
                    asset={selectedAssetMeta}
                    alarmIds={alarms.filter(a => a.status === 'open').map(a => a.alarmId)}
                    onSuccess={handleBaselineSuccess}
                />
            </div>
        </CleanModernLayout>
    );
}

export default DriftAlarms;
